import React, { useState } from 'react';
import { Lock, X } from 'lucide-react';

const DURATIONS = [
  { id: '1', label: '1 day', days: 1 },
  { id: '3', label: '3 days', days: 3 },
  { id: '7', label: '1 week', days: 7 },
  { id: '14', label: '2 weeks', days: 14 },
  { id: 'date', label: 'Until a date' },
  { id: 'manual', label: 'Until I unlock' },
];

export const lockoutActive = (m) => !!m?.lockout?.active && (!m.lockout.until || new Date(m.lockout.until) > new Date());
export const lockoutLabel = (m) => {
  if (!lockoutActive(m)) return '';
  if (!m.lockout.until) return 'Locked out';
  return `Locked until ${new Date(m.lockout.until).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`;
};

export default function LockoutModal({ member, onConfirm, onClose }) {
  const [choice, setChoice] = useState('7');
  const [until, setUntil] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const picked = DURATIONS.find(d => d.id === choice);
  const canSubmit = choice !== 'date' || !!until;

  const submit = async () => {
    setBusy(true);
    const payload = { reason };
    if (choice === 'date') payload.until = until;
    else if (picked?.days) payload.days = picked.days;
    try { await onConfirm(payload); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="lockout-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2"><Lock className="h-4 w-4 text-red-600" /> Lock out {member.full_name}</h3>
          <button onClick={onClose} className="p-1 text-slate-500 hover:text-slate-700" data-testid="lockout-close-btn"><X className="h-4 w-4" /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <p className="text-xs text-slate-600">They won't be able to sign in to Ops or clock in at the shop kiosk, and any session they have open is kicked out. They stay on the schedule, dropdowns and reports as normal.</p>
          <div>
            <p className="text-xs font-semibold text-slate-700 mb-2">How long?</p>
            <div className="grid grid-cols-3 gap-2">
              {DURATIONS.map(d => (
                <button key={d.id} type="button" onClick={() => setChoice(d.id)} data-testid={`lockout-duration-${d.id}`}
                  className={`px-2 py-1.5 rounded-lg text-xs font-medium border transition-colors ${choice === d.id ? 'bg-red-600 border-red-600 text-white' : 'bg-white border-slate-300 text-slate-700 hover:border-red-400'}`}>
                  {d.label}
                </button>
              ))}
            </div>
            {choice === 'date' && (
              <input type="date" value={until} min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)} onChange={e => setUntil(e.target.value)}
                className="mt-2 w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm" data-testid="lockout-until-input" />
            )}
            {choice === 'date' && until && <p className="text-[11px] text-slate-500 mt-1">Access comes back at 12:00 AM on that day.</p>}
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-1">Reason <span className="font-normal text-slate-500">(optional, internal)</span></label>
            <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} placeholder="e.g. Suspended pending review"
              className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm" data-testid="lockout-reason-input" />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900" data-testid="lockout-cancel-btn">Cancel</button>
          <button onClick={submit} disabled={!canSubmit || busy} data-testid="lockout-confirm-btn"
            className="px-4 py-1.5 inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
            <Lock className="h-3.5 w-3.5" /> {busy ? 'Locking…' : 'Lock out'}
          </button>
        </div>
      </div>
    </div>
  );
}
